import {
  ManualTriggerInput,
  ManualTriggerInputType,
  ManualTriggerPortalForm,
  StepError,
} from '@/types/automationSteps.ts';
import { Center, CloseButton, Group, Stack } from '@mantine/core';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import * as React from 'react';
import { capitalizeFirstLetter } from '@/utils/misc.ts';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import StyledCheckbox from '@/components/StyledInputFields/StyledCheckbox.tsx';
import { useHover } from '@mantine/hooks';
import useIsNewStep from '@/hooks/useIsNewStep.ts';
import { STEP_FIELD_DESCRIPTION_MAX_LENGTH, STEP_FIELD_MAX_LENGTH } from '@/utils/constants.ts';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea.tsx';
import JsonSchemaRefSelect from '@/pages/Editor/components/SharedFields/JsonSchemaRefSelect.tsx';
import { AutomationJsonSchema, SchemaValidationMode } from '@/types/automationFields.ts';
import { isObjectOnlySchema } from '@/utils/schemaShapes.ts';

type props = {
  manualTriggerInput: ManualTriggerInput;
  onChangeNoDelay: (updatedInput: ManualTriggerInput) => void;
  onChangeDelay: (updatedInput: ManualTriggerInput) => void;
  onDelete: () => void;
  stepId: string;
  error?: StepError;
  schemas: AutomationJsonSchema[];
};

const ManualTriggerInputFields = ({
  manualTriggerInput,
  onChangeNoDelay,
  onChangeDelay,
  onDelete,
  stepId,
  error,
  schemas,
}: props) => {
  const { hovered, ref: hoverRef } = useHover();

  const isNewStep = useIsNewStep(stepId);

  return (
    <Stack gap={'4px'}>
      <Group gap={'xs'} wrap={'nowrap'} align={'start'}>
        <StyledSelect
          label="Type"
          data={Object.values(ManualTriggerInputType).map((type) => ({
            value: type,
            label: capitalizeFirstLetter(type),
          }))}
          value={manualTriggerInput.input_type}
          onChange={(value) =>
            onChangeNoDelay({
              ...manualTriggerInput,
              input_type: value as ManualTriggerInputType,
              schema_ref: value === ManualTriggerInputType.Json ? null : undefined,
              portal_form:
                value === ManualTriggerInputType.Json ? manualTriggerInput.portal_form : undefined,
            })
          }
          required={true}
          allowDeselect={false}
          clearable={false}
          w={'90px'}
        />

        <StyledTextInput
          label="Name"
          value={manualTriggerInput.name}
          placeholder="Input Name"
          onChange={(event) => {
            const value = event.currentTarget.value.replace(/[/\n]/g, '');
            onChangeDelay({ ...manualTriggerInput, name: value });
          }}
          required={true}
          maxLength={STEP_FIELD_MAX_LENGTH}
          w={'100%'}
          error={error && !isNewStep ? error.message : undefined}
        />
        <Center h={'36px'} mt={'28px'}>
          <CloseButton
            onClick={() => onDelete()}
            ref={hoverRef}
            style={{
              background: hovered ? '#inherit' : 'inherit',
              color: hovered ? '#9E9E9E' : '#BABABA',
            }}
            size={'sm'}
          />
        </Center>
      </Group>
      {manualTriggerInput.input_type === ManualTriggerInputType.Json && (
        <>
          <JsonSchemaRefSelect
            label="JSON schema"
            description="Required. JSON manual inputs must use an error-mode schema that allows only objects."
            schemas={schemas.filter((schema) => isObjectOnlySchema(schema.json_schema))}
            value={manualTriggerInput.schema_ref}
            required
            requiredValidationMode={SchemaValidationMode.Error}
            onChange={(schema_ref) => onChangeNoDelay({ ...manualTriggerInput, schema_ref })}
          />
          <PortalFormDefinitionEditor
            portalForm={manualTriggerInput.portal_form}
            onChange={(portal_form) => onChangeNoDelay({ ...manualTriggerInput, portal_form })}
          />
        </>
      )}
      <StyledTextArea
        label="Description"
        value={manualTriggerInput.description}
        placeholder="Instructions shown to people filling out this input"
        onChange={(event) =>
          onChangeDelay({ ...manualTriggerInput, description: event.currentTarget.value })
        }
        maxLength={STEP_FIELD_DESCRIPTION_MAX_LENGTH}
        minRows={2}
      />
      <Group justify={'flex-end'} mr={'35px'}>
        <StyledCheckbox
          label={'Required'}
          checked={manualTriggerInput.is_required}
          onChange={(event) =>
            onChangeNoDelay({ ...manualTriggerInput, is_required: event.currentTarget.checked })
          }
          // size={'xs'}
          styles={{
            root: { '--checkbox-size': '12px' },
            label: { fontSize: '11px', paddingLeft: '8px' },
            inner: { marginTop: '4px' },
          }}
        />
      </Group>
    </Stack>
  );
};

function PortalFormDefinitionEditor({
  portalForm,
  onChange,
}: {
  portalForm: ManualTriggerPortalForm | null | undefined;
  onChange: (portalForm: ManualTriggerPortalForm | null) => void;
}) {
  const [text, setText] = React.useState(() =>
    portalForm ? JSON.stringify(portalForm, null, 2) : '',
  );
  const [parseError, setParseError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setText(portalForm ? JSON.stringify(portalForm, null, 2) : '');
    setParseError(null);
  }, [portalForm]);

  return (
    <StyledTextArea
      label="Customer portal form"
      description="Optional for internal JSON inputs; required to enable a public portal. Define customer-editable fields only. The server validates this definition against the selected schema."
      placeholder={'{\n  "title": "Request details",\n  "fields": []\n}'}
      value={text}
      error={parseError || undefined}
      minRows={8}
      onChange={(event) => {
        const nextText = event.currentTarget.value;
        setText(nextText);
        if (!nextText.trim()) {
          setParseError(null);
          onChange(null);
          return;
        }
        try {
          const parsed: unknown = JSON.parse(nextText);
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            setParseError('Portal form must be a JSON object.');
            return;
          }
          const candidate = parsed as Partial<ManualTriggerPortalForm>;
          if (!Array.isArray(candidate.fields)) {
            setParseError('Portal form must include a fields array.');
            return;
          }
          setParseError(null);
          onChange(candidate as ManualTriggerPortalForm);
        } catch {
          setParseError('Portal form must contain valid JSON.');
        }
      }}
    />
  );
}

export default ManualTriggerInputFields;
